import type { App as InertiaApp } from '@inertiajs/react';
import { type ComponentProps, type ComponentType, StrictMode } from 'react';
import { IntlProvider } from 'react-intl';

import OverlayContainer from '../components/OverlayContainer/OverlayContainer';
import StyleReset from '../components/StyleReset/StyleReset';
import ThemeProvider from '../components/ThemeProvider/ThemeProvider';
import dashboardTheme from '../shared/dashboardTheme';
import { ConfirmDialogProvider } from '../tenant-dashboard/hooks/ui/useConfirmDialog';

import type PageProps from './types/inertia/PageProps';

/*
 * @inertiajs/react does not publicly export the setup callback's option type,
 * so derive the props contract from the App component itself (instantiated with
 * the app's PageProps, exactly as the entrypoint calls it).
 */
type InertiaAppProps = ComponentProps<typeof InertiaApp<PageProps>>;

type RootProps = {
    App: ComponentType<InertiaAppProps>;
    props: InertiaAppProps;
    translations: Record<string, string>;
};

const Root = ({ App, props, translations }: RootProps) => {
    const { localization } = props.initialPage.props;
    const { ietfLanguageTag } = localization.currentLocale;

    return (
        <StrictMode>
            <StyleReset>
                <ThemeProvider theme={dashboardTheme}>
                    <IntlProvider
                        messages={translations}
                        locale={ietfLanguageTag}
                        onError={(err) => {
                            // TODO: Probably send this to Sentry
                            console.error(err);
                        }}
                    >
                        <OverlayContainer>
                            <ConfirmDialogProvider>
                                <App {...props} />
                            </ConfirmDialogProvider>
                        </OverlayContainer>
                    </IntlProvider>
                </ThemeProvider>
            </StyleReset>
        </StrictMode>
    );
};

export default Root;
